// src/pages/projects/projectDocumentService.js

import {
  collection, addDoc, updateDoc, doc,
  getDocs, query, where, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase";

// ── Document Types ────────────────────────────────────────────────────────────
export const DOC_TYPES = {
  drawing_shop:     { label: "Shop Drawing",       icon: "📐", color: "bg-blue-100 text-blue-700" },
  drawing_asbuilt:  { label: "As-Built Drawing",   icon: "🗺️", color: "bg-green-100 text-green-700" },
  drawing_approval: { label: "Approval Drawing",   icon: "✅", color: "bg-purple-100 text-purple-700" },
  boq:              { label: "BOQ",                icon: "📋", color: "bg-orange-100 text-orange-700" },
  work_order:       { label: "Work Order",         icon: "📝", color: "bg-gray-100 text-gray-700" },
  quotation:        { label: "Quotation",          icon: "💰", color: "bg-yellow-100 text-yellow-700" },
  inspection:       { label: "Inspection Report",  icon: "🔍", color: "bg-red-100 text-red-700" },
  test_cert:        { label: "Test Certificate",   icon: "🏆", color: "bg-teal-100 text-teal-700" },
  other:            { label: "Other",              icon: "📄", color: "bg-slate-100 text-slate-700" },
};

export const DOC_STATUS = {
  draft:      { label: "Draft",     color: "bg-gray-100 text-gray-600" },
  submitted:  { label: "Submitted", color: "bg-blue-100 text-blue-700" },
  approved:   { label: "Approved",  color: "bg-green-100 text-green-700" },
  rejected:   { label: "Rejected",  color: "bg-red-100 text-red-700" },
  superseded: { label: "Superseded",color: "bg-yellow-100 text-yellow-600" },
};

// ── Upload Document ───────────────────────────────────────────────────────────
export const uploadProjectDocument = async ({
  file,
  company_id,
  project_id,
  project_name,
  document_type,
  document_name,
  checklist_item_no = null,
  remarks           = "",
  uploaded_by,
  uploaded_by_name,
}) => {
  if (!file)         throw new Error("File required");
  if (!company_id)   throw new Error("company_id required");
  if (!project_id)   throw new Error("project_id required");
  if (!document_type) throw new Error("Document type required");

  // ── Get current version number ──
  const existing = await getDocs(query(
    collection(db, "project_documents"),
    where("company_id",    "==", company_id),
    where("project_id",    "==", project_id),
    where("document_type", "==", document_type),
  ));

  const currentMax = existing.docs.reduce((max, d) => {
    const v = d.data().version || 0;
    return v > max ? v : max;
  }, 0);

  const newVersion      = currentMax + 1;
  const versionLabel    = `v${newVersion}`;
  const finalDocName    = document_name || `${DOC_TYPES[document_type]?.label} ${versionLabel}`;

  // ── Upload to Firebase Storage ──
  const folder    = ["boq", "quotation", "work_order"].includes(document_type) ? "documents" :
                    document_type.startsWith("drawing") ? "drawings" : "documents";
  const timestamp = Date.now();
  const safeName  = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const storagePath = `companies/${company_id}/projects/${project_id}/${folder}/${timestamp}_${safeName}`;

  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file);
  const file_url = await getDownloadURL(storageRef);

  // ── Mark previous versions as superseded ──
  if (newVersion > 1) {
    const prevDocs = existing.docs.filter(d => d.data().is_latest === true);
    await Promise.all(prevDocs.map(d =>
      updateDoc(doc(db, "project_documents", d.id), {
        is_latest:  false,
        status:     "superseded",
        updated_at: serverTimestamp(),
      })
    ));
  }

  // ── Save document record ──
  const docRef = await addDoc(collection(db, "project_documents"), {
    company_id,
    project_id,
    project_name:      project_name || "",
    document_type,
    document_name:     finalDocName,
    file_url,
    file_name:         file.name,
    file_size:         file.size,
    file_type:         file.type,
    storage_path:      storagePath,
    version:           newVersion,
    version_label:     versionLabel,
    status:            "submitted",
    checklist_item_no: checklist_item_no || null,
    remarks:           remarks || "",
    is_latest:         true,
    uploaded_by:       uploaded_by      || "",
    uploaded_by_name:  uploaded_by_name || "",
    approved_by:       "",
    approved_by_name:  "",
    approved_at:       null,
    created_by:        uploaded_by      || "",
    created_by_name:   uploaded_by_name || "",
    created_at:        serverTimestamp(),
    updated_at:        serverTimestamp(),
  });

  return { success: true, doc_id: docRef.id, version: newVersion, file_url };
};

// ── Fetch Project Documents ───────────────────────────────────────────────────
export const fetchProjectDocuments = async (project_id, company_id) => {
  const snap = await getDocs(query(
    collection(db, "project_documents"),
    where("project_id", "==", project_id),
    where("company_id", "==", company_id),
  ));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
};

// ── Update Document Status ────────────────────────────────────────────────────
export const updateDocumentStatus = async (doc_id, status, user) => {
  await updateDoc(doc(db, "project_documents", doc_id), {
    status,
    approved_by:      status === "approved" ? (user.uid || user.id || "") : "",
    approved_by_name: status === "approved" ? (user.name || "") : "",
    approved_at:      status === "approved" ? serverTimestamp() : null,
    updated_at:       serverTimestamp(),
  });
};